import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown, ChevronRight, Link2, Loader2, LogIn, Play, Plug, RefreshCw, Square, Unlink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { magKoppelen } from "./NextcloudConnection";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import { zoekIn } from "@/lib/koppelcatalogus";
import { INLOGGEN, koppelStatus, STATUS_TEKST, VERBONDEN } from "@/lib/koppelstatus";
import KoppelIcoon from "./KoppelIcoon";
import { BUITEN_EU, EU, LOCATIE_TEKST, LOKAAL, ONBEKEND } from "@/lib/aanbieder-locatie";

/**
 * Instellingen > Verbindingen > MCP-servers. I6, 17 oktober 2026.
 *
 * Koppelen (naam, adres, optionele sleutel), toetsen, aan/uit en ontkoppelen,
 * op de routes van I1 en I2. Alleen https en niet naar deze machine; dat zegt
 * de backend en de zin komt van daar. De sleutel gaat na het koppelen uit de
 * staat en komt nooit terug: de lijst zegt alleen OF er een is.
 *
 * Een kijker ziet zijn lijst, maar geen formulier en geen knoppen (Frank, I6);
 * de backend weigert hem ook (403).
 *
 * I3: ook een LOKAAL PROGRAMMA uit de vaste lijst van de backend. Koppelen start
 * niets. "Starten" opent een kaart met het EXACTE commando; pas "Bevestig en
 * start" zet het in gang, met het WF-40-token van die kaart, een keer.
 */

/** De argumenten uit het tekstvak: een map per regel. PUUR. */
/**
 * CC1 (besluit Frank 24 oktober 2026): geen harde stop meer bij twintig koppelingen. Vanaf
 * vijftig, of de laatste tien voor een lagere grens van de organisatie, een waarschuwing.
 * De backend beslist OF; dit zegt het in de taal van het scherm. PUUR op `toast` na.
 */
export function waarschuw(uit, t) {
  if (!uit?.waarschuwing) return false;
  toast.warning(t("Je hebt nu {aantal} koppelingen; de grens van deze organisatie is {grens}.",
    { aantal: uit.koppelingen_aantal, grens: uit.koppelingen_grens }));
  return true;
}

export function argumentenUit(tekst) {
  return (tekst || "").split("\n").map(r => r.trim()).filter(Boolean);
}

function StartKaart({ server, onGestart, onSluit }) {
  const { t } = useTaal();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const { token, vervallen, laatVervallen } = useKaartToken(`${API}/mcp-servers/${server.id}/start/toon`, true);
  const start = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/mcp-servers/${server.id}/start`, { token });
      onGestart(r.data);
    } catch (e) {
      const reden = e.response?.data?.detail || t("Starten is niet gelukt.");
      setFout(reden);
      laatVervallen(reden);
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="border rounded-md p-2 space-y-2 bg-muted/40" data-testid="mcp-startkaart">
      <p className="text-xs">{t("Dit commando start op deze machine:")}</p>
      <pre className="text-xs whitespace-pre-wrap break-all font-mono" data-testid="mcp-startcommando">{server.commando}</pre>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={bezig || !token} className={vervallen ? GRIJS : ""} onClick={start}
                data-testid="mcp-bevestig-start">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
          {t("Bevestig en start")}
        </Button>
        <Button size="sm" variant="outline" disabled={bezig} onClick={onSluit} data-testid="mcp-start-annuleer">
          {t("Annuleren")}
        </Button>
      </div>
      {fout && <p className="text-xs text-destructive" data-testid="mcp-startfout">{fout}</p>}
    </div>
  );
}
/** B6 deel 1 (GL216, 29 okt): wat NovaMund met dit gereedschap mag, uit `rechten` van de
 *  backend (dezelfde regel als de chat en de agents). Zonder opgave niets. PUUR. */
export function rechtTekst(recht, t) {
  if (!recht) return "";
  if (recht.direct) return t("Leest direct");
  const soort = {
    versturen: t("verstuurt iets"), betalen: t("een betaling"),
    ondertekenen: t("ondertekent of keurt goed"), verwijderen: t("verwijdert iets"),
  }[recht.akkoord];
  return soort ? t("Na jouw klik: {soort}", { soort }) : t("Na jouw klik");
}

export function gereedschapTekst(server, t) {
  const n = (server.gereedschappen || []).length;
  if (!server.getoetst) return t("Nog niet getoetst");
  return n === 1 ? t("1 gereedschap") : `${n} ${t("gereedschappen")}`;
}

// W1a: `zoek` komt van het zoekveld in het venster Koppelingen; Instellingen geeft
// niets mee en ziet dus de hele lijst, zoals voorheen.
export default function McpServers({ zoek = "" } = {}) {
  const { t } = useTaal();
  const [servers, setServers] = useState([]);
  const [rol, setRol] = useState("");
  const [laden, setLaden] = useState(true);
  const [naam, setNaam] = useState("");
  // CC2 (besluit Frank 24 okt 2026): Eigen netwerk bij het koppelen; alleen een eigenaar.
  const [eigenNetwerk, setEigenNetwerk] = useState(false);
  const [adres, setAdres] = useState("");
  const [sleutel, setSleutel] = useState("");
  const [bezig, setBezig] = useState("");
  const [fout, setFout] = useState("");
  const [programmas, setProgrammas] = useState([]);
  const [programma, setProgramma] = useState("");
  const [lokaalNaam, setLokaalNaam] = useState("");
  const [mappen, setMappen] = useState("");
  const [startkaart, setStartkaart] = useState("");

  const lees = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mcp-servers`);
      setServers(Array.isArray(r.data) ? r.data.filter(s => s.actief) : []);
    } catch {
      setServers([]);
    }
    try {
      const p = await axios.get(`${API}/mcp-servers/programmas`);
      setProgrammas(Array.isArray(p.data) ? p.data : []);
    } catch {
      setProgrammas([]);
    }
    try {
      const o = await axios.get(`${API}/organisatie`);
      setRol(o.data?.mijn_rol || "");
    } catch {
      setRol("");
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { lees(); }, [lees]);

  const vervang = (rij) => setServers(lijst => lijst.map(s => (s.id === rij.id ? rij : s)).filter(s => s.actief));

  const doe = async (wat, werk, gelukt) => {
    setBezig(wat);
    setFout("");
    try {
      await werk();
      if (gelukt) toast.success(gelukt);
    } catch (e) {
      const reden = e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd.");
      setFout(reden);
      toast.error(reden);
    } finally {
      setBezig("");
    }
  };

  const koppel = () => doe("koppel", async () => {
    const r = await axios.put(`${API}/mcp-servers`, { naam, adres, sleutel, ...(eigenNetwerk ? { eigen_netwerk: true } : {}) });
    waarschuw(r.data, t);
    setServers(lijst => [r.data, ...lijst]);
    setNaam(""); setAdres(""); setSleutel(""); setEigenNetwerk(false);
  }, t("MCP-server gekoppeld"));

  const gekozen = programmas.find(p => p.id === programma);
  const koppelLokaal = () => doe("lokaal", async () => {
    const r = await axios.put(`${API}/mcp-servers/lokaal`, {
      naam: lokaalNaam, programma, argumenten: gekozen?.argumenten ? argumentenUit(mappen) : [],
    });
    waarschuw(r.data, t);
    setServers(lijst => [r.data, ...lijst]);
    setLokaalNaam(""); setMappen(""); setProgramma("");
  }, t("Lokaal programma gekoppeld. Het draait pas na Starten."));

  const stopLokaal = (s) => doe(`stop-${s.id}`, async () => {
    const r = await axios.post(`${API}/mcp-servers/${s.id}/stop`);
    vervang(r.data);
  }, t("Lokaal programma gestopt"));

  const toets = (s) => doe(`toets-${s.id}`, async () => {
    const r = await axios.post(`${API}/mcp-servers/${s.id}/toets`);
    vervang(r.data.server);
  }, t("MCP-server getoetst"));

  // W1g: inloggen bij een server die OAuth vraagt, zoals bij Google: de backend zet
  // een luisteraar op deze Mac en geeft de URL; jij logt in in de browser; daarna
  // wisselt de backend de code in en bewaart de inlog versleuteld. Dan opnieuw toetsen.
  const inloggen = (s) => doe(`inlog-${s.id}`, async () => {
    const start = await axios.post(`${API}/mcp-servers/${s.id}/inloggen/start`);
    window.open(start.data.url, "_blank", "noopener");
    const klaar = await axios.post(`${API}/mcp-servers/${s.id}/inloggen/klaar/${start.data.id}`);
    vervang(klaar.data);
    const r = await axios.post(`${API}/mcp-servers/${s.id}/toets`);
    vervang(r.data.server);
  }, t("Ingelogd bij de server"));

  // Herstelbatch g (GO Frank 23 okt): de gereedschappen per koppeling in- en
  // uitklapbaar, standaard ingeklapt; het aantal staat op de knop.
  const [uitgeklapt, setUitgeklapt] = useState(() => new Set());
  const klap = (id) => setUitgeklapt((oud) => {
    const nieuw = new Set(oud);
    if (nieuw.has(id)) nieuw.delete(id); else nieuw.add(id);
    return nieuw;
  });

  const zetAan = (s, aan) => doe(`aan-${s.id}`, async () => {
    const r = await axios.patch(`${API}/mcp-servers/${s.id}`, { aan });
    vervang(r.data);
  });

  // W1e: een gereedschap van deze koppeling aan of uit, gedeeld met de agentkaart.
  // CC2-a: de schakelaar per koppeling, alleen voor een eigenaar.
  const zetEigenNetwerk = (s, aan) => doe(`eigen-${s.id}`, async () => {
    const r = await axios.patch(`${API}/mcp-servers/${s.id}/eigen-netwerk`, { aan });
    vervang(r.data);
  }, aan ? t("Eigen netwerk staat aan voor deze koppeling") : t("Eigen netwerk staat uit voor deze koppeling"));

  const zetGereedschap = (s, tool, aan) => doe(`tool-${s.id}-${tool}`, async () => {
    const r = await axios.patch(`${API}/mcp-servers/${s.id}/gereedschap`, { tool, aan });
    vervang(r.data);
  });

  // W1j: waar de host staat. Een lokaal programma staat vast op deze Mac.
  const zetLocatie = (s, locatie) => doe(`plek-${s.id}`, async () => {
    const r = await axios.patch(`${API}/mcp-servers/${s.id}/locatie`, { locatie });
    vervang(r.data);
  });

  const ontkoppel = (s) => doe(`weg-${s.id}`, async () => {
    const r = await axios.delete(`${API}/mcp-servers/${s.id}`);
    vervang(r.data);
  }, t("MCP-server ontkoppeld"));

  if (laden) return <Loader2 className="h-4 w-4 animate-spin" aria-label={t("Laden")} />;
  const mag = magKoppelen(rol);
  const getoond = zoekIn(servers, zoek, [(s) => s.naam, (s) => s.adres, (s) => s.commando]);

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="bron-mcp">
      <div className="flex items-start gap-3">
        <Plug className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t("MCP-servers")}</p>
          <p className="text-xs text-muted-foreground">
            {t("Servers die gereedschap geven aan de chat en aan je agents. Lezen en zoeken gaat direct; bestellen, een link maken of iets aanvragen wacht eerst op je bevestiging.")}
          </p>
        </div>
      </div>

      {fout && (
        <p className="text-xs text-[hsl(var(--accent))]" data-testid="fout-mcp">
          <AlertTriangle className="h-3 w-3 inline mr-1" />{fout}
        </p>
      )}

      {servers.length > 0 && getoond.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="mcp-zoek-leeg">
          {t("Geen koppeling met \"{zoek}\"", { zoek })}
        </p>
      )}
      {getoond.length > 0 && (
        <ul className="space-y-2" data-testid="mcp-lijst">
          {getoond.map(s => (
            <li key={s.id} className="border rounded-md p-2 space-y-1" data-testid="mcp-server-rij">
              <div className="flex items-center gap-2">
                <KoppelIcoon server={s} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm truncate">{s.naam}</p>
                    {/* W1d: de status, met de zin van de fout als uitleg erbij. */}
                    <span data-testid="mcp-status" data-status={koppelStatus(s)}
                          className={`text-[10px] rounded-full border px-1.5 leading-tight shrink-0 ${koppelStatus(s) === VERBONDEN ? "" : "text-[hsl(var(--accent))]"}`}>
                      {t(STATUS_TEKST[koppelStatus(s)])}
                    </span>
                  </div>
                  {s.omschrijving && (
                    <p className="text-xs text-muted-foreground" data-testid="mcp-omschrijving">{s.omschrijving}</p>
                  )}
                  <p className="text-xs text-muted-foreground break-all">{s.soort === "stdio" ? s.commando : s.adres}</p>
                  {/* W1j (blok L): de plek van de host, onbekend tot iemand hem zet. */}
                  {mag && s.soort !== "stdio" && s.actief ? (
                    <select value={s.locatie || ONBEKEND} disabled={bezig === `plek-${s.id}`}
                            onChange={e => zetLocatie(s, e.target.value)} aria-label={t("Locatie")}
                            className="text-xs border rounded px-1 py-0.5 bg-background" data-testid="mcp-locatie-keuze">
                      {[EU, BUITEN_EU, ONBEKEND].map(plek => (
                        <option key={plek} value={plek}>{t(LOCATIE_TEKST[plek])}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="mcp-locatie">
                      {t(LOCATIE_TEKST[s.soort === "stdio" ? LOKAAL : (s.locatie || ONBEKEND)] || LOCATIE_TEKST[ONBEKEND])}
                    </p>
                  )}
                  {/* CC2-a: Eigen netwerk per koppeling; zetten mag alleen een eigenaar. */}
                  {s.soort !== "stdio" && s.actief && (rol === "eigenaar" ? (
                    <label className="flex items-center gap-2 text-xs" data-testid="mcp-eigen-netwerk">
                      <Switch checked={Boolean(s.eigen_netwerk)} disabled={bezig === `eigen-${s.id}`}
                              onCheckedChange={(v) => zetEigenNetwerk(s, v)} aria-label={t("Eigen netwerk")} />
                      {t("Eigen netwerk")}
                    </label>
                  ) : s.eigen_netwerk && (
                    <p className="text-xs text-muted-foreground" data-testid="mcp-eigen-netwerk-tekst">{t("Eigen netwerk")}</p>
                  ))}
                  {s.soort === "stdio" && (
                    <p className="text-xs" data-testid="mcp-draait">{s.draait ? t("Draait") : t("Gestopt")}</p>
                  )}
                  {s.fout && (
                    <p className="text-xs text-[hsl(var(--accent))]" data-testid="mcp-foutzin">{s.fout}</p>
                  )}
                  <p className="text-xs text-muted-foreground" data-testid="mcp-stand">
                    {gereedschapTekst(s, t)}{s.heeft_sleutel ? ` - ${t("met sleutel")}` : ""}
                  </p>
                </div>
                {mag && (
                  <Switch checked={s.aan} disabled={bezig === `aan-${s.id}`}
                          onCheckedChange={v => zetAan(s, v)} aria-label={t("Aan")}
                          data-testid="mcp-aan" />
                )}
              </div>
              {(s.gereedschappen || []).length > 0 && (
                <button type="button" className="flex items-center gap-1 pl-10 text-xs text-muted-foreground min-h-11"
                        aria-expanded={uitgeklapt.has(s.id)} onClick={() => klap(s.id)}
                        data-testid="mcp-gereedschappen-knop">
                  {uitgeklapt.has(s.id) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {t("Gereedschappen ({n}, {aan} aan)", {
                    n: s.gereedschappen.length,
                    aan: s.gereedschappen.filter((g) => !(s.gereedschap_uit || []).includes(g.naam)).length })}
                </button>
              )}
              {(s.gereedschappen || []).length > 0 && uitgeklapt.has(s.id) && (
                <ul className="space-y-1 pl-10" data-testid="mcp-gereedschappen">
                  {s.gereedschappen.map((g) => {
                    const aan = !(s.gereedschap_uit || []).includes(g.naam);
                    return (
                      <li key={g.naam} className="flex items-center gap-2 text-xs" data-testid="mcp-gereedschap">
                        <span className={`min-w-0 flex-1 truncate ${aan ? "" : "text-muted-foreground line-through"}`}
                              title={g.beschrijving || ""}>{g.titel || g.naam}</span>
                        {aan && rechtTekst((s.rechten || {})[g.naam], t) && (
                          <span className="shrink-0 text-muted-foreground" data-testid={`mcp-recht-${g.naam}`}>
                            {rechtTekst((s.rechten || {})[g.naam], t)}
                          </span>
                        )}
                        {mag ? (
                          <Switch checked={aan} disabled={bezig === `tool-${s.id}-${g.naam}`}
                                  onCheckedChange={(v) => zetGereedschap(s, g.naam, v)}
                                  aria-label={t("{naam} aan", { naam: g.titel || g.naam })}
                                  data-testid={`mcp-gereedschap-aan-${g.naam}`} />
                        ) : (
                          <span className="text-muted-foreground">{aan ? t("Aan") : t("Uit")}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {mag && (
                <div className="flex flex-wrap gap-2">
                  {s.soort === "stdio" && !s.draait && s.aan && (
                    <Button variant="outline" size="sm" disabled={!!bezig} onClick={() => setStartkaart(s.id)}
                            data-testid="mcp-starten">
                      <Play className="h-3 w-3 mr-1" />{t("Starten")}
                    </Button>
                  )}
                  {s.soort === "stdio" && s.draait && (
                    <Button variant="outline" size="sm" disabled={!!bezig} onClick={() => stopLokaal(s)}
                            data-testid="mcp-stoppen">
                      <Square className="h-3 w-3 mr-1" />{t("Stoppen")}
                    </Button>
                  )}
                  {s.soort !== "stdio" && koppelStatus(s) === INLOGGEN && (
                    <Button size="sm" disabled={!!bezig} onClick={() => inloggen(s)} data-testid="mcp-inloggen">
                      {bezig === `inlog-${s.id}` ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <LogIn className="h-3 w-3 mr-1" />}
                      {t("Inloggen")}
                    </Button>
                  )}
                  {(s.soort !== "stdio" || s.draait) && (
                  <Button variant="outline" size="sm" disabled={!!bezig} onClick={() => toets(s)}
                          data-testid="mcp-toets">
                    {bezig === `toets-${s.id}` ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      : <RefreshCw className="h-3 w-3 mr-1" />}
                    {t("Toetsen")}
                  </Button>
                  )}
                  <Button variant="outline" size="sm" disabled={!!bezig} onClick={() => ontkoppel(s)}
                          data-testid="mcp-ontkoppel">
                    <Unlink className="h-3 w-3 mr-1" />{t("Ontkoppelen")}
                  </Button>
                </div>
              )}
              {mag && startkaart === s.id && (
                <StartKaart server={s} onSluit={() => setStartkaart("")}
                            onGestart={(rij) => { vervang(rij); setStartkaart(""); toast.success(t("Lokaal programma gestart")); }} />
              )}
            </li>
          ))}
        </ul>
      )}

      {!mag ? (
        <p className="text-xs text-muted-foreground" data-testid="kijker-mcp">
          {t("Je bent kijker in deze organisatie. Alleen een eigenaar of lid kan MCP-servers koppelen.")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="mcp-formulier">
          {/* W1b: alleen het adres is genoeg; de naam is een keuze. */}
          <Input value={naam} onChange={e => setNaam(e.target.value)} placeholder={t("Naam (optioneel)")}
                 aria-label={t("Naam (optioneel)")} data-testid="mcp-naam" />
          <Input value={adres} onChange={e => setAdres(e.target.value)}
                 placeholder="https://mcp.example.com/mcp" aria-label={t("Serveradres")}
                 data-testid="mcp-adres" />
          <Input type="password" value={sleutel} onChange={e => setSleutel(e.target.value)}
                 placeholder={t("Sleutel (optioneel)")} aria-label={t("Sleutel (optioneel)")}
                 autoComplete="new-password" data-testid="mcp-sleutel" />
          {rol === "eigenaar" && (
            <label className="flex items-center gap-2 text-xs" data-testid="mcp-nieuw-eigen-netwerk">
              <Switch checked={eigenNetwerk} onCheckedChange={setEigenNetwerk} aria-label={t("Eigen computer of eigen netwerk")} />
              {t("Eigen computer of eigen netwerk")}
            </label>
          )}
          {eigenNetwerk && (
            <p className="text-xs text-muted-foreground" data-testid="mcp-eigen-netwerk-uitleg">
              {t("Alleen voor een adres op je eigen netwerk (bijvoorbeeld 192.168.x of Tailscale). Dan mag ook http, maar alleen naar zo'n adres. Deze machine zelf blijft dicht; een programma op deze Mac koppel je als lokaal programma.")}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {t("Alleen https. Een sleutel hoort in het sleutelveld, niet in het adres.")}{" "}
            {t("Zonder naam krijgt de server bij de eerste toets de naam die hij zelf opgeeft.")}
          </p>
          <Button size="sm" disabled={!!bezig || !adres.trim()} onClick={koppel}
                  data-testid="mcp-koppel">
            {bezig === "koppel" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
            {t("Koppelen")}
          </Button>
        </div>
      )}

      {mag && programmas.length > 0 && (
        <div className="space-y-2 border-t pt-3" data-testid="mcp-lokaal-formulier">
          <p className="text-xs font-medium">{t("Lokaal programma")}</p>
          <select value={programma} onChange={e => setProgramma(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  aria-label={t("Programma")} data-testid="mcp-programma">
            <option value="">{t("Kies een programma")}</option>
            {programmas.map(p => <option key={p.id} value={p.id}>{p.naam}</option>)}
          </select>
          {gekozen && (
            <p className="text-xs text-muted-foreground font-mono break-all" data-testid="mcp-programma-commando">
              {gekozen.commando}
            </p>
          )}
          <Input value={lokaalNaam} onChange={e => setLokaalNaam(e.target.value)} placeholder={t("Naam")}
                 aria-label={t("Naam")} data-testid="mcp-lokaal-naam" />
          {gekozen?.argumenten === "mappen" && (
            <textarea value={mappen} onChange={e => setMappen(e.target.value)} rows={2}
                      placeholder="/Users/frank/Documenten" aria-label={t("Mappen, een per regel")}
                      className="w-full rounded-md border bg-background px-2 py-1 text-sm font-mono"
                      data-testid="mcp-mappen" />
          )}
          <p className="text-xs text-muted-foreground">
            {t("Alleen programma's van deze lijst. NovaMund installeert niets; het pakket moet er al staan.")}
          </p>
          <Button size="sm" disabled={!!bezig || !programma || !lokaalNaam.trim()} onClick={koppelLokaal}
                  data-testid="mcp-lokaal-koppel">
            <Link2 className="h-3 w-3 mr-1" />{t("Koppelen")}
          </Button>
        </div>
      )}
    </div>
  );
}
